import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import { BUSINESS } from "@/lib/config";

export default function TermsConditions() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <main className="pt-28 pb-16 md:pt-36 md:pb-24" data-testid="terms-page">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            data-testid="terms-back-link"
            className="inline-flex items-center gap-2 text-[#00296B] font-semibold text-sm hover:text-[#FFD500] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>

          <div className="text-xs uppercase tracking-[0.3em] text-[#334155] font-semibold mb-4">
            — Legal
          </div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl text-[#00296B] leading-tight mm-underline">
            Terms &amp; Conditions
          </h1>
          <p className="mt-6 text-sm text-[#334155]">
            Last updated: {new Date().toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })}
          </p>

          <div className="mt-12 space-y-10 text-[#334155] leading-relaxed">
            <Section title="1. Acceptance of Terms">
              <p>
                By accessing or using the {BUSINESS.name} website and by
                sending an enquiry through our forms, WhatsApp, phone or email,
                you agree to be bound by these Terms &amp; Conditions. If you
                do not agree, please do not use this website.
              </p>
            </Section>

            <Section title="2. About Us">
              <p>
                {BUSINESS.name} is an electrical wholesale and retail showroom
                located at {BUSINESS.address.line1} {BUSINESS.address.line2}{" "}
                {BUSINESS.address.line3}. We deal in electrical products,
                accessories, wires, cables, lighting, fans, geysers,
                switchgear and related items from reputed Indian brands.
              </p>
            </Section>

            <Section title="3. Website Content">
              <p>
                The information, product images, brand logos and descriptions
                on this website are provided for general information only.
                Product availability, specifications, features and pricing may
                change without notice. Brand logos and product photos are the
                trademarks and property of their respective owners and are
                used here for identification purposes only.
              </p>
            </Section>

            <Section title="4. Enquiries &amp; Quotations">
              <p>
                Enquiries submitted via our contact form, WhatsApp or phone
                are considered requests for information only. A confirmed
                quotation is only valid when issued in writing (WhatsApp,
                email or printed invoice) by an authorised representative of{" "}
                {BUSINESS.name}. Prices quoted are valid for the specific
                duration mentioned in that quotation.
              </p>
            </Section>

            <Section title="5. Products &amp; Warranty">
              <p>
                All products sold by us are genuine and are covered by the
                manufacturer's warranty. Warranty terms, service centres and
                claim procedures are as per the respective brand. We do not
                offer any additional warranty beyond that of the manufacturer,
                unless explicitly agreed in writing.
              </p>
            </Section>

            <Section title="6. Payment">
              <p>
                Payments can be made via cash, UPI, bank transfer, cheque or
                card at our showroom. Goods remain the property of{" "}
                {BUSINESS.name} until full payment has been received.
              </p>
            </Section>

            <Section title="7. Delivery">
              <p>
                Delivery timelines shared verbally or in quotations are
                indicative and depend on stock availability and courier /
                transport partners. We are not liable for delays caused by
                third-party logistics or events beyond our control.
              </p>
            </Section>

            <Section title="8. Returns &amp; Exchange">
              <p>
                Electrical products once sold can only be returned or exchanged
                as per the manufacturer's policy and only if the product is
                unused, in original packaging, and accompanied by the original
                bill. Custom-cut wires, cut-to-length cables and installed
                items are non-returnable.
              </p>
            </Section>

            <Section title="9. Limitation of Liability">
              <p>
                {BUSINESS.name} shall not be liable for any indirect,
                incidental or consequential damages arising out of the use or
                misuse of any product purchased from us. Installation of
                electrical products should be done by a certified
                electrician; we are not responsible for damages arising out of
                improper installation.
              </p>
            </Section>

            <Section title="10. Intellectual Property">
              <p>
                All content on this website — including the Master Mall logo,
                text, layout and design — is owned by {BUSINESS.name} and may
                not be copied, reproduced or used without our written
                permission.
              </p>
            </Section>

            <Section title="11. Governing Law">
              <p>
                These Terms shall be governed by and construed in accordance
                with the laws of India. Any dispute arising from these Terms
                or your dealings with {BUSINESS.name} shall be subject to the
                exclusive jurisdiction of the courts at Nashik, Maharashtra.
              </p>
            </Section>

            <Section title="12. Changes to Terms">
              <p>
                We may revise these Terms at any time without prior notice.
                The updated version will be posted on this page with a new
                "Last updated" date. Continued use of the website constitutes
                acceptance of the revised Terms.
              </p>
            </Section>

            <Section title="13. Contact Us">
              <p>For any questions about these Terms, please contact:</p>
              <div className="mt-4 p-6 bg-[#F8F9FA] border border-[#00296B]/10">
                <p className="font-semibold text-[#00296B]">{BUSINESS.name}</p>
                <p className="mt-1 text-sm">
                  {BUSINESS.address.line1} {BUSINESS.address.line2}{" "}
                  {BUSINESS.address.line3}
                </p>
                <p className="mt-2 text-sm">
                  Phone:{" "}
                  <a
                    href={`tel:${BUSINESS.phoneRaw}`}
                    className="text-[#00296B] font-semibold underline"
                  >
                    {BUSINESS.phoneDisplay}
                  </a>
                </p>
                <p className="text-sm">
                  Email:{" "}
                  <a
                    href={`mailto:${BUSINESS.email}`}
                    className="text-[#00296B] font-semibold underline"
                  >
                    {BUSINESS.email}
                  </a>
                </p>
              </div>
            </Section>
          </div>
        </div>
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#00296B] leading-tight mb-4">
        {title}
      </h2>
      <div className="text-base sm:text-lg space-y-3">{children}</div>
    </section>
  );
}
